import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Cloud, 
  Database, 
  Globe, 
  HardDrive,
  CheckCircle2,
  XCircle,
  AlertCircle,
  RefreshCw,
  Terminal,
  Link,
  Server,
  Activity
} from 'lucide-react';

interface InfraStatus {
  phase: number;
  name: string;
  status: string;
  components: {
    pages: any;
    workers: any;
    database: any;
    storage: any;
  };
  checklist: {
    pages: boolean;
    workers: boolean;
    database: boolean;
    r2_storage: boolean;
    bcdn_bucket: boolean;
  };
}

const Phase1Infrastructure: React.FC = () => {
  const [status, setStatus] = useState<InfraStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [activeTest, setActiveTest] = useState<string | null>(null);
  const [testResults, setTestResults] = useState<any>({});

  const API_BASE = 'https://biig-api.b-9f2.workers.dev';

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/phase1/status`);
      const data = await res.json();
      setStatus(data);
    } catch (error) {
      console.error('Error fetching status:', error);
    } finally {
      setLoading(false);
    }
  };

  const runTest = async (test: string) => {
    setActiveTest(test);
    try {
      const res = await fetch(`${API_BASE}/api/phase1/test/${test}`);
      const data = await res.json();
      setTestResults((prev: any) => ({ ...prev, [test]: data }));
    } catch (error) {
      console.error(`Error running ${test} test:`, error);
      setTestResults((prev: any) => ({ ...prev, [test]: { error: true } }));
    } finally {
      setActiveTest(null);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  const getStatusIcon = (isActive: boolean) => {
    return isActive ? (
      <CheckCircle2 className="w-5 h-5 text-green-400" />
    ) : (
      <XCircle className="w-5 h-5 text-red-400" />
    );
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'deployed':
      case 'active':
      case 'ready':
        return 'text-green-400';
      case 'pending':
        return 'text-yellow-400';
      case 'error':
        return 'text-red-400';
      default:
        return 'text-gray-400';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-4xl font-bold text-white mb-2 flex items-center gap-3">
            <Cloud className="w-10 h-10 text-blue-400" />
            Phase 1: Infrastructure Without a Domain
          </h1>
          <p className="text-gray-300">
            Cloudflare Pages • Workers • D1 Database • R2 Storage
          </p>
        </motion.div>

        {/* Quick Actions */}
        <div className="flex gap-4 mb-8">
          <button
            onClick={fetchStatus}
            disabled={loading}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh Status
          </button>
          <a
            href="https://biig-web.pages.dev"
            target="_blank"
            rel="noopener noreferrer"
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2"
          >
            <Link className="w-4 h-4" />
            View Frontend
          </a>
          <a
            href={`${API_BASE}/api/health`}
            target="_blank"
            rel="noopener noreferrer"
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
          >
            <Activity className="w-4 h-4" />
            API Health
          </a>
        </div>

        {/* Infrastructure Grid */}
        {status && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            {/* Pages */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-gray-800 rounded-xl p-6"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <Globe className="w-8 h-8 text-blue-400" />
                  <div>
                    <h3 className="text-xl font-semibold text-white">
                      Cloudflare Pages
                    </h3>
                    <span className={`text-sm ${getStatusColor(status.components.pages.status)}`}>
                      {status.components.pages.status}
                    </span>
                  </div>
                </div>
                {getStatusIcon(status.checklist.pages)}
              </div>
              <div className="space-y-2">
                <div className="text-sm">
                  <span className="text-gray-400">URL:</span>
                  <a href={status.components.pages.url} target="_blank" rel="noopener noreferrer" 
                     className="ml-2 text-blue-400 hover:underline">
                    {status.components.pages.url}
                  </a>
                </div>
                <div className="text-sm">
                  <span className="text-gray-400">Deployments:</span>
                  <span className="ml-2 text-white">{status.components.pages.deployments?.length || 0}</span>
                </div>
              </div>
            </motion.div>

            {/* Workers */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.1 }}
              className="bg-gray-800 rounded-xl p-6"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <Server className="w-8 h-8 text-green-400" />
                  <div>
                    <h3 className="text-xl font-semibold text-white">
                      Cloudflare Workers
                    </h3>
                    <span className={`text-sm ${getStatusColor(status.components.workers.status)}`}>
                      {status.components.workers.status}
                    </span>
                  </div>
                </div>
                {getStatusIcon(status.checklist.workers)}
              </div>
              <div className="space-y-2">
                <div className="text-sm">
                  <span className="text-gray-400">URL:</span>
                  <a href={status.components.workers.url} target="_blank" rel="noopener noreferrer"
                     className="ml-2 text-blue-400 hover:underline">
                    {status.components.workers.url}
                  </a>
                </div>
                <div className="text-sm">
                  <span className="text-gray-400">Version:</span>
                  <span className="ml-2 text-white font-mono text-xs">
                    {status.components.workers.version?.substring(0, 8)}...
                  </span>
                </div>
                <button
                  onClick={() => runTest('endpoints')}
                  disabled={activeTest === 'endpoints'}
                  className="mt-2 px-3 py-1 bg-gray-700 text-white rounded text-sm hover:bg-gray-600"
                >
                  {activeTest === 'endpoints' ? 'Testing...' : 'Test Endpoints'}
                </button>
              </div>
            </motion.div>

            {/* Database */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="bg-gray-800 rounded-xl p-6"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <Database className="w-8 h-8 text-yellow-400" />
                  <div>
                    <h3 className="text-xl font-semibold text-white">
                      D1 Database
                    </h3>
                    <span className={`text-sm ${getStatusColor(status.components.database.status)}`}>
                      {status.components.database.status}
                    </span>
                  </div>
                </div>
                {getStatusIcon(status.checklist.database)}
              </div>
              <div className="space-y-2">
                <div className="text-sm">
                  <span className="text-gray-400">Name:</span>
                  <span className="ml-2 text-white">{status.components.database.dbName}</span>
                </div>
                <div className="text-sm">
                  <span className="text-gray-400">Tables:</span>
                  <span className="ml-2 text-white">{status.components.database.tables}</span>
                </div>
                <div className="text-sm">
                  <span className="text-gray-400">Size:</span>
                  <span className="ml-2 text-white">{status.components.database.size}</span>
                </div>
                <button
                  onClick={() => runTest('database')}
                  disabled={activeTest === 'database'}
                  className="mt-2 px-3 py-1 bg-gray-700 text-white rounded text-sm hover:bg-gray-600"
                >
                  {activeTest === 'database' ? 'Testing...' : 'Test Connection'}
                </button>
              </div>
            </motion.div>

            {/* Storage */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3 }}
              className="bg-gray-800 rounded-xl p-6"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <HardDrive className="w-8 h-8 text-purple-400" />
                  <div>
                    <h3 className="text-xl font-semibold text-white">
                      R2 Storage
                    </h3>
                    <span className={`text-sm ${getStatusColor(status.components.storage.status)}`}>
                      {status.components.storage.status}
                    </span>
                  </div>
                </div>
                {getStatusIcon(status.checklist.r2_storage)}
              </div>
              <div className="space-y-2">
                {status.components.storage.buckets?.map((bucket: any, index: number) => (
                  <div key={index} className="text-sm flex items-center gap-2">
                    {bucket.status === 'active' ? (
                      <CheckCircle2 className="w-4 h-4 text-green-400" />
                    ) : bucket.status === 'pending' ? (
                      <AlertCircle className="w-4 h-4 text-yellow-400" />
                    ) : (
                      <XCircle className="w-4 h-4 text-red-400" />
                    )}
                    <span className="text-white">{bucket.name}</span>
                    {bucket.note && (
                      <span className="text-gray-500 text-xs">({bucket.note})</span>
                    )}
                  </div>
                ))}
                <button
                  onClick={() => runTest('storage')}
                  disabled={activeTest === 'storage'}
                  className="mt-2 px-3 py-1 bg-gray-700 text-white rounded text-sm hover:bg-gray-600"
                >
                  {activeTest === 'storage' ? 'Testing...' : 'Test Storage'}
                </button>
              </div>
            </motion.div>
          </div>
        )}

        {/* Test Results */}
        {Object.keys(testResults).length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gray-800 rounded-xl p-6 mb-8"
          >
            <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
              <Terminal className="w-5 h-5" />
              Test Results
            </h3>
            <div className="space-y-4">
              {Object.entries(testResults).map(([test, result]: [string, any]) => (
                <div key={test} className="bg-gray-700 rounded-lg p-4">
                  <h4 className="text-white font-medium mb-2 capitalize">{test} Test</h4>
                  {result.error ? (
                    <div className="text-red-400">Test failed</div>
                  ) : (
                    <pre className="text-xs text-gray-300 overflow-x-auto">
                      {JSON.stringify(result, null, 2)}
                    </pre>
                  )}
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Checklist */}
        {status && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gray-800 rounded-xl p-6"
          >
            <h3 className="text-xl font-semibold text-white mb-4">Infrastructure Checklist</h3>
            <div className="space-y-3">
              {Object.entries(status.checklist).map(([item, completed]) => (
                <div key={item} className="flex items-center gap-3">
                  {completed ? (
                    <CheckCircle2 className="w-5 h-5 text-green-400" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-yellow-400" />
                  )}
                  <span className="text-white capitalize">
                    {item.replace(/_/g, ' ')}
                  </span>
                  {item === 'bcdn_bucket' && !completed && (
                    <code className="ml-auto text-xs bg-gray-700 px-2 py-1 rounded text-gray-300">
                      npx wrangler r2 bucket create bcdn
                    </code>
                  )}
                </div>
              ))}
            </div>
            <div className="mt-6 p-4 bg-gray-700 rounded-lg">
              <p className="text-sm text-gray-300">
                <strong className="text-white">Status:</strong>{' '}
                {Object.values(status.checklist).every(v => v) 
                  ? '✅ All infrastructure components operational'
                  : '⚠️ Some components need configuration'}
              </p>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default Phase1Infrastructure;